/**
 * Apollo Client — single execution point for all GraphQL data access.
 *
 * Architecture (FERP 5-layer):
 *   Layer 1: Types         → data/portal/types/
 *   Layer 2: Client        → this file (singleton)
 *   Layer 3: Repositories  → data/portal/repositories/ (constructor DI)
 *   Layer 4: Hooks         → data/portal/hooks/ (consume repositories)
 *   Layer 5: UI Components → pages/ and components/
 *
 * All subgraph queries route through Apollo Router which composes
 * the federated schema from ferp-subgraphs endpoints.
 */
import { ApolloClient, InMemoryCache, HttpLink } from '@apollo/client/core';
import type { TypePolicies } from '@apollo/client/cache';

const PORTAL_GRAPHQL_ENDPOINT =
  import.meta.env.VITE_PORTAL_GRAPHQL_ENDPOINT || '/portal/graphql';

const typePolicies: TypePolicies = {
  GqlPortal: { keyFields: ['id'] },
  GqlExposedWorkflow: { keyFields: ['id'] },
  GqlFormSubmission: { keyFields: ['id'] },
};

export function createApolloClient(): ApolloClient {
  const httpLink = new HttpLink({
    uri: PORTAL_GRAPHQL_ENDPOINT,
  });

  return new ApolloClient({
    link: httpLink,
    cache: new InMemoryCache({ typePolicies }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all',
      },
      query: {
        fetchPolicy: 'cache-first',
        errorPolicy: 'all',
      },
      mutate: {
        errorPolicy: 'all',
      },
    },
    devtools: { enabled: import.meta.env.DEV },
  });
}

/** Singleton Apollo Client — injected into repositories and ApolloProvider */
export const apolloClient = createApolloClient();
